import {
  Tabs as TabsBase,
  TabsContent,
  TabsList
} from "@/common/components/ui/tabs";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { cn } from "@/common/utils/cn";
import QueryString from "qs";
import { useEffect, useMemo, useState } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import TabTrigger from "./components/TabTrigger";
import TabTriggerLoading from "./components/TabTriggerLoading";

export interface TabsProps {
  tabsList: {
    value: string;
    label: string;
    disabled?: boolean;
    body: JSX.Element | string;
    hidden?: boolean;
    operationKlassToCheckPermission?: string;
    shouldHideIfUserHasNoPermission?: boolean;
  }[];
  defaultValue?: string;
  tabsClassName?: string;
  tabsListClassName?: string;
  tabsListRef?: any;
  tabsTriggerClassName?: string;
  tabsContentClassName?: string;
  keepFilterParams?: boolean;
  filterParamsOnTabChange?: { [key: string]: any };
  isLoading?: boolean;
  skeletonsCount?: number;
  noRouting?: boolean;
  currentTab?: string;
  setCurrentTab?: (value: string | undefined) => void;
  BeforeTabsComponent?: JSX.Element;
  AfterTabsComponent?: JSX.Element;
  EmptyTabsComponent?: JSX.Element;
}

const Tabs = ({
  tabsList,
  defaultValue = "",
  tabsClassName,
  tabsListClassName,
  tabsListRef,
  tabsTriggerClassName,
  tabsContentClassName,
  keepFilterParams,
  filterParamsOnTabChange,
  isLoading,
  skeletonsCount = 10,
  noRouting,
  currentTab: noRoutingCurrentTab,
  setCurrentTab: setNoRoutingCurrentTab,
  BeforeTabsComponent,
  AfterTabsComponent,
  EmptyTabsComponent,
}: TabsProps) => {
  const { queryString } = useFilterParams();
  const { hasUserPermission } = useUserPermissions();

  const navigate = useNavigate();
  const location = useLocation();

  const [internalNoRoutingCurrentTab, setInternalNoRoutingCurrentTab] = useState<string>();

  const filteredTabsList = useMemo(() => {
    return tabsList.filter((tab) => {
      if (tab.hidden) return false;

      if (!tab.operationKlassToCheckPermission) return true;

      const userHasPermission = hasUserPermission(
        tab.operationKlassToCheckPermission,
      );
      const shouldHideIfUserHasNoPermission =
        tab.shouldHideIfUserHasNoPermission ?? true;

      if (userHasPermission) return true;
      return !shouldHideIfUserHasNoPermission;
    });
  }, [tabsList, hasUserPermission]);

  const initialPath = useMemo(() => {
    const pathnameParts = location.pathname.split("/");
    const lastPath = pathnameParts[pathnameParts.length - 1];

    const allTabsValues = tabsList.map((tab) => tab.value);

    if (allTabsValues.includes(lastPath || "")) {
      pathnameParts.pop();
      return pathnameParts.join("/");
    }

    return location.pathname;
  }, [tabsList, location.pathname]);

  const currentTab = useMemo(() => {
    const last = location.pathname.slice(initialPath.length).split("/").pop();
    return last ? last : undefined;
  }, [initialPath, location.pathname]);

  const computedDefaultValue = useMemo(() => {
    if (!filteredTabsList.length) return defaultValue;
    const defaultExists = filteredTabsList.some(
      (tab) => tab.value === defaultValue,
    );
    if (defaultExists) return defaultValue;
    return filteredTabsList[0]?.value ?? defaultValue;
  }, [defaultValue, filteredTabsList]);

  useEffect(() => {
    if (!filteredTabsList.length) return;
    if (!currentTab) return;

    const currentTabIsAllowed = filteredTabsList.some(
      (t) => t.value === currentTab,
    );
    if (currentTabIsAllowed) return;

    const nextValue = computedDefaultValue ?? defaultValue;

    let path = nextValue ? `${initialPath}/${nextValue}` : initialPath;

    if (keepFilterParams) {
      if (filterParamsOnTabChange) {
        path += `?${QueryString.stringify(filterParamsOnTabChange)}`;
      } else {
        path += `?${queryString}`;
      }
    }

    if (!noRouting)
      navigate(path, { replace: true });
  }, [
    currentTab,
    filteredTabsList,
    computedDefaultValue,
    defaultValue,
    initialPath,
    keepFilterParams,
    filterParamsOnTabChange,
    navigate,
    queryString,
    noRouting,
  ]);

  const handleTabChange = (value: string) => {
    let path = value ? `${initialPath}/${value}` : initialPath;

    if (keepFilterParams) {
      if (filterParamsOnTabChange) {
        path += `?${QueryString.stringify(filterParamsOnTabChange)}`;
      } else {
        path += `?${queryString}`;
      }
    }

    if (path.startsWith('//')) {
      path = path.replace('//', '/');
    }

    if (!noRouting)
      navigate(path);
    else
      setInternalNoRoutingCurrentTab(value);
  };

  useEffect(() => {
    setNoRoutingCurrentTab?.(internalNoRoutingCurrentTab);
  }, [internalNoRoutingCurrentTab]);

  useEffect(() => {
    setInternalNoRoutingCurrentTab(noRoutingCurrentTab);
  }, [noRoutingCurrentTab]);

  return (
    <TabsBase
      value={
        !noRouting
          ? currentTab ?? computedDefaultValue
          : internalNoRoutingCurrentTab ?? filteredTabsList?.[0]?.value
      }
      onValueChange={handleTabChange}
      className={tabsClassName}
    >
      {isLoading && (
        <TabTriggerLoading
          tabsListClassName={tabsListClassName}
          tabsTriggerClassName={tabsTriggerClassName}
          skeletonsCount={skeletonsCount}
        />
      )}

      <div className={cn(
        "flex items-center",
        isLoading && "invisible max-h-0"
      )}>
        {BeforeTabsComponent}

        <TabsList className={cn('flex-1', tabsListClassName)} ref={tabsListRef}>
          {filteredTabsList.map((tab, index) => (
            <TabTrigger
              key={index}
              value={tab.value}
              label={tab.label}
              className={tabsTriggerClassName}
              disabled={tab.disabled}
              operationKlassToCheckPermission={
                tab.operationKlassToCheckPermission
              }
            />
          ))}
        </TabsList>

        {AfterTabsComponent}
      </div>

      {!noRouting && (
        <Routes>
          {filteredTabsList.map((content) => {
            if (content.value === "") {
              return (
                <Route
                  key="__tabs_index__"
                  path=""
                  element={
                    <TabsContent
                      value={content.value}
                      className={tabsContentClassName}
                    >
                      {content.body}
                    </TabsContent>
                  }
                />
              );
            }

            return (
              <Route
                key={content.value}
                path={content.value}
                element={
                  <TabsContent
                    value={content.value}
                    className={tabsContentClassName}
                  >
                    {content.body}
                  </TabsContent>
                }
              />
            );
          })}
        </Routes>
      )}

      {noRouting && (
        <>
          {filteredTabsList.map((content) => (
            <TabsContent
              key={content.value}
              value={content.value}
              className={tabsContentClassName}
            >
              {content.body}
            </TabsContent>
          ))}
        </>
      )}

      {!isLoading && filteredTabsList.length === 0 && EmptyTabsComponent}
    </TabsBase>
  );
};

export default Tabs;
